import { OpponentListResponseDto } from "@/lib/api/interfaces/opponents.interface";
import { useOpponentsDelete, useOpponentsFindQuery, useOpponentsBulkDelete } from "@/lib/api/requests/opponents.requests";
import { getAbbreviatedTeamName } from "@/lib/utils/team-utils";
import {
  DataView,
  DataViewContent,
  DataViewEmptyState,
  DataViewGridCard,
  DataViewLayout,
  DataViewListCard,
  DataViewLoadingState,
  DataViewToolbar,
  GenericItem,
  GenericItemActions,
  EntityPreviewFrame
} from "@/util-components/data-view";
import { PrimaryActionButton } from "@/util-components/page-layout";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { Flag, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "@/lib/toast";
import CreateOpponentDialog from "./create-dialog";
import OpponentDetailsDialog from "./details-dialog";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";

const OpponentsList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  
  const { data: opponents, isLoading, refetch } = useOpponentsFindQuery(searchTerm.trim() || undefined);
  const { mutate: deleteOpponent, isPending: isDeleting } = useOpponentsDelete();
  const { mutate: bulkDeleteOpponents, isPending: isBulkDeleting } = useOpponentsBulkDelete();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsPopupOpen, setDetailsPopupOpen] = useState<{
    open: boolean;
    opponentId: string | null;
    mode: "details" | "edit";
  }>({
    open: false,
    opponentId: null,
    mode: "details",
  });

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; opponent: OpponentListResponseDto | null; isBulk?: boolean }>({
    open: false,
    opponent: null,
    isBulk: false
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleDelete = async () => {
    if (deleteConfirm.isBulk && selectedIds.length > 0) {
      bulkDeleteOpponents({ ids: selectedIds }, {
        onSuccess: () => {
            toast.success(`${selectedIds.length} Gegner erfolgreich gelöscht`);
            setDeleteConfirm({ open: false, opponent: null, isBulk: false });
            setSelectedIds([]);
            refetch();
        },
        onError: (error) => {
            toast.error("Gegner konnten nicht gelöscht werden", { error });
        }
      });
      return;
    }

    if (!deleteConfirm.opponent) return;

    deleteOpponent({ id: deleteConfirm.opponent._id }, {
      onSuccess: () => {
          toast.success("Gegner erfolgreich gelöscht");
          setDeleteConfirm({ open: false, opponent: null });
          refetch();
      },
      onError: (error) => {
          toast.error("Gegner konnte nicht gelöscht werden", { error });
      }
    });
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setDeleteConfirm({ open: true, opponent: null, isBulk: true });
    }
  };

  const convertToGenericItem = useCallback((opponent: OpponentListResponseDto): GenericItem => {
    const abbreviation = opponent.name ? getAbbreviatedTeamName(opponent.name, {preferLeadingNumber: false}) : "";
    const logoUrl = opponent.thumbnailUrl || opponent.logoUrl;

    return {
      id: opponent._id,
      title: opponent.name,
      description: "Gegner",
      previewContent: (
        <EntityPreviewFrame backgroundUrl={logoUrl}>
          {logoUrl ? (
            <img 
              src={logoUrl}
              alt={opponent.name} 
              className="max-h-full max-w-full object-contain" 
            />
          ) : opponent.name ? (
            <span className="text-2xl font-bold tracking-tight text-blue-600 uppercase">
              {abbreviation}
            </span>
          ) : (
            <Flag className="h-1/2 w-1/2 text-blue-600 opacity-50" />
          )}
        </EntityPreviewFrame>
      ),
      previewContentList: (
        <div className="w-full h-full flex items-center justify-center bg-blue-50">
            {logoUrl ? (
                <img src={logoUrl} alt="" className="h-6 w-6 object-contain" />
            ) : opponent.name ? (
                <span className="text-[10px] font-bold text-blue-600 uppercase">
                    {abbreviation}
                </span>
            ) : (
                <Flag className="h-4 w-4 text-blue-600 opacity-50" />
            )}
        </div>
      )
    };
  }, []);

  const genericItems = useMemo(() => {
    return Array.isArray(opponents) ? opponents.map(convertToGenericItem) : [];
  }, [opponents, convertToGenericItem]);

  const genericActions: GenericItemActions = {
    onItemClick: (id) => {
      setDetailsPopupOpen({ open: true, opponentId: id, mode: "details" });
    },
    onEdit: (id) => {
      setDetailsPopupOpen({ open: true, opponentId: id, mode: "edit" });
    },
    onDelete: (id) => {
      const opponent = opponents?.find(o => o._id === id);
      if (opponent) setDeleteConfirm({ open: true, opponent });
    }
  };

  return (
    <div className="h-full flex flex-col">
       {/* Note: PageHeader is handled in parent wrapper usually, but here we might just use DataView directly 
           if we are inside a Tab. If we are inside a Tab, we might want to skip the "Opponents" title header 
           if the tab itself is the header. Let's assume this component fills the tab content.
       */}
       
      <DataView 
        viewMode={viewMode} 
        onViewModeChange={setViewMode}
        className="flex-1 bg-transparent"
        isLoading={isLoading}
        isEmpty={genericItems.length === 0}
        selectedIds={selectedIds}
        onMultiSelect={setSelectedIds}
        onSelectionDelete={handleSelectionDelete}
      >
        <div className="flex flex-col h-full">
          <DataViewToolbar
            searchQuery={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Gegner suchen..."
            infoText={
              `${opponents?.length || 0} Gegner`
            }
            
            leading={
              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <PrimaryActionButton 
                    onClick={() => setCreateDialogOpen(true)}
                    label="Gegner hinzufügen"
                    icon={Plus}
                />
              </PermissionFeatureWrapper>
            }
          />

          <DataViewLayout>
            <DataViewContent
              items={genericItems}
              renderGridItem={(item) => (
                <DataViewGridCard
                  item={item}
                  actions={genericActions}
                />
              )}
              renderListItem={(item) => (
                <DataViewListCard
                  item={item}
                  actions={genericActions}
                />
              )}
              loadingIndicator={<DataViewLoadingState />}
              emptyState={
                <DataViewEmptyState
                  icon={<Flag className="h-8 w-8 text-gray-400" />}
                  title={searchTerm ? 'Keine Gegner gefunden' : 'Keine Gegner'}
                  description={searchTerm 
                    ? 'Versuchen Sie andere Suchbegriffe.'
                    : 'Erstellen Sie Ihren ersten Gegner.'
                  }
                  action={
                    !searchTerm ? (
                      <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                        <PrimaryActionButton 
                            onClick={() => setCreateDialogOpen(true)}
                            label="Gegner erstellen"
                            icon={Plus}
                        />
                      </PermissionFeatureWrapper>
                    ) : undefined
                  }
                />
              }
            />
          </DataViewLayout>
        </div>

        {/* Dialogs */}
        {detailsPopupOpen.open && detailsPopupOpen.opponentId && (
          <OpponentDetailsDialog
            open={detailsPopupOpen.open}
            opponentId={detailsPopupOpen.opponentId}
            mode={detailsPopupOpen.mode}
            onClose={(refetchData) => {
                setDetailsPopupOpen({ open: false, opponentId: null, mode: "details" });
                if (refetchData) refetch();
            }}
          />
        )}

        {createDialogOpen && (
          <CreateOpponentDialog
            open={createDialogOpen}
            onClose={(refetchData) => {
              setCreateDialogOpen(false);
              if (refetchData) {
                refetch();
              }
            }}
          />
        )}
        
        <StandardDeleteDialog 
          open={deleteConfirm.open} 
          onOpenChange={(val) => setDeleteConfirm(prev => ({ ...prev, open: val }))}
          onDelete={handleDelete}
          title={deleteConfirm.isBulk ? `${selectedIds.length} Gegner löschen` : "Gegner löschen"}
          description={deleteConfirm.isBulk ? "Möchten Sie diese Gegner wirklich unwiderruflich löschen?" : "Diese Aktion kann nicht rückgängig gemacht werden"}
          itemTitle={deleteConfirm.opponent?.name}
          itemTypeLabel={deleteConfirm.opponent ? "ID" : undefined}
          itemTypeValue={deleteConfirm.opponent?._id}
          itemsList={deleteConfirm.isBulk ? selectedIds.map(id => {
            const o = opponents?.find(o => o._id === id);
            return {
              title: o ? o.name : id
            };
          }) : undefined}
          isLoading={isDeleting || isBulkDeleting}
        />
      </DataView>
    </div>
  );
};

export default OpponentsList;
